import React from "react";
import _ from "lodash";
import { FormattedMessage, useIntl } from "react-intl";
import { ColumnDefinition, RowDefinition } from "griddle-react";
import { Button, ButtonGroup } from "reactstrap";
import { Link } from "react-router-dom";

import { Track as WataTrack } from "@music/models";
import { Table, TableProps } from "@ui/Table";
import { Collapsable } from "@ui/Table/Collapsable";
import { CellDateTime } from "@ui/Table/Cells";
import { CellPlaylistsList } from "../PlaylistsList";
import { Track } from "../../models";
import { messages } from "../../messages";

interface TrackListTableProps {
    tracks: Track[];
    isPlaylist?: boolean;
    collapsable?: boolean;
}

interface Row {
    entity: Track;
}

// Type the table component
type TypedTable = new () => Table<Row>;
const TypedTable = Table as TypedTable;

type Props = TrackListTableProps;

const TRACKS_LIMIT = 7;

const CellTrackLink = ({ value }: { value: number }) => {
    return (
        <ButtonGroup>
            {value != null &&
                <Button
                    tag={Link}
                    to={(new WataTrack({id: value})).getViewUrl()}
                    size="sm"
                    outline
                >
                    <FormattedMessage {...messages.goToTrack} />
                </Button>
            }
        </ButtonGroup>
    );
};

const CellNumber = ({ value }: { value: number }) => (
    <span>{value + 1}</span>
);

const CellVendorLink = ({ value }: { value: Track }) => {
    const track = new Track(value.toJS());
    let elt = <span>{track.vendorName}</span>;
    if (track.url != null) {
        elt = <a href={track.url} target="_blank">{track.vendorName}</a>;
    }
    return elt;
};

const TrackListTable = ({ tracks, isPlaylist, collapsable }: Props) => {
    const data = _.map(tracks, (track) => {
        return {entity: track} as Row;
    });
    const tableProps: TableProps<Row> = {
        data,
        pageSize: data.length,
        layout: "basic",
        size: "small",
    };
    let hasAddedAt = false;
    if (tracks.length > 0) {
        hasAddedAt = tracks[0].pivot != null && tracks[0].pivot.addedAt != null;
    }
    const intl = useIntl();

    const tableDefinition = (
        <RowDefinition cssClassName={
            ({ rowData }) => rowData.entity.trackId != null && isPlaylist
                ? "table-primary"
                : ""
        }>
            {isPlaylist &&
                <ColumnDefinition
                    key="number"
                    id="entity.pivot.order"
                    title="#"
                    customComponent={CellNumber}
                />
            }
            <ColumnDefinition
                key="title"
                id="entity.title"
                title={intl.formatMessage(messages.trackColumnTitle)}
            />
            <ColumnDefinition
                key="artists"
                id="entity.artists"
                title={intl.formatMessage(messages.trackColumnArtists)}
            />
            <ColumnDefinition
                key="album"
                id="entity.album"
                title={intl.formatMessage(messages.trackColumnAlbum)}
            />
            <ColumnDefinition
                key="isrc"
                id="entity.isrc"
                title={intl.formatMessage(messages.trackColumnIsrc)}
            />
            {isPlaylist && hasAddedAt &&
                <ColumnDefinition
                    key="addedAt"
                    id="entity.pivot.addedAt"
                    title={intl.formatMessage(messages.playlistSnapshotTrackColumnAddedAt)}
                    customComponent={CellDateTime}
                />
            }
            {!isPlaylist &&
                <ColumnDefinition
                    key="playlists"
                    id="entity.playlists"
                    title={intl.formatMessage(messages.trackColumnPlaylists)}
                    customComponent={CellPlaylistsList}
                />
            }
            <ColumnDefinition
                key="vendor"
                id="entity"
                title={intl.formatMessage(messages.trackColumnVendor)}
                customComponent={CellVendorLink}
            />
            <ColumnDefinition
                key="buttons"
                id="entity.trackId"
                title=" "
                customComponent={CellTrackLink}
            />
        </RowDefinition>
    );

    return (
        <div className="vendor-track-list">
            {!collapsable && <Table {...tableProps}>{tableDefinition}</Table>}
            {collapsable && <Collapsable<Row> limit={TRACKS_LIMIT} {...tableProps}>{tableDefinition}</Collapsable>}
        </div>
    );
};

export {
    TrackListTable,
    Props as TrackListTableProps,
    CellVendorLink,
    CellTrackLink,
    CellNumber,
};
